import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi, clearAdminToken, getAdminToken } from "../lib/api";
import { toast } from "../hooks/use-toast";
import { LogOut, RefreshCw, ChevronLeft, Mail, Calendar, Users, Building2, Send, Copy, CheckCircle2, UserCheck, UserX, Clock, Video, Briefcase, Settings as SettingsIcon, Hourglass, Download, FileDown, Megaphone, Trash2 } from "lucide-react";
import CMSWebinars from "./admin/CMSWebinars";
import CMSOrganisations from "./admin/CMSOrganisations";
import CMSOpportunities from "./admin/CMSOpportunities";
import CMSSettings from "./admin/CMSSettings";
import CMSEmailTemplates from "./admin/CMSEmailTemplates";
import CMSNetworkUpdates from "./admin/CMSNetworkUpdates";
import CMSMembers from "./admin/CMSMembers";

const STATUSES = ["new", "reviewing", "approved", "declined"];
const STATUS_STYLES = {
  new: { bg: '#ece5d3', color: '#0d2a20' },
  reviewing: { bg: '#fde5c7', color: '#8a4a10' },
  approved: { bg: '#d6ecd6', color: '#154a1c' },
  declined: { bg: '#f3d6d1', color: '#7a1f13' },
};
const BUCKET_STYLES = {
  applicant: { bg: '#ece5d3', color: '#0d2a20', label: 'Applicant' },
  waitlisted: { bg: '#e8dcc4', color: '#5a4a1a', label: 'Waitlisted' },
  approved_pending: { bg: '#fde5c7', color: '#8a4a10', label: 'Approved · pending activation' },
  active_member: { bg: '#d6ecd6', color: '#154a1c', label: 'Active member' },
  declined: { bg: '#f3d6d1', color: '#7a1f13', label: 'Declined' },
};

const Pill = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.new;
  return <span className="px-2 py-1 rounded text-[11px] font-medium uppercase tracking-wider" style={{ background: s.bg, color: s.color }}>{status}</span>;
};

const BucketPill = ({ bucket }) => {
  const s = BUCKET_STYLES[bucket] || BUCKET_STYLES.applicant;
  return <span className="px-2 py-1 rounded text-[11px] font-medium" style={{ background: s.bg, color: s.color }}>{s.label}</span>;
};

const fmtDate = (iso) => { try { return iso ? new Date(iso).toLocaleString() : "—"; } catch { return iso; } };

const DetailPanel = ({ scope, app, onBack, onStatus, onResend, resending, onDelete }) => {
  if (!app) return null;
  const hidden = new Set(["_id", "id", "status", "created_at", "member", "bucket", "cv_storage_path"]);
  const entries = Object.entries(app).filter(([k]) => !hidden.has(k));
  const isJoin = scope === 'join';
  const hasCv = isJoin && !!(app.cv_storage_path || "").trim();

  const downloadCv = async () => {
    try {
      const res = await adminApi.get(`/admin/applications/join/${app.id}/cv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = app.cv_filename || `cv-${app.id}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast({ title: "Download failed", description: "Could not fetch the CV. Please try again." });
    }
  };
  return (
    <div className="rounded-lg p-8" style={{ background: '#fbf8ef' }}>
      <button onClick={onBack} className="inline-flex items-center gap-1 text-[13px] mb-6 link-underline"><ChevronLeft size={14} /> Back to list</button>
      <div className="flex items-start justify-between gap-6 mb-6 flex-wrap">
        <div>
          <div className="section-eyebrow text-[#0d2a20]/60 mb-2">{isJoin ? 'Join application' : 'Partner inquiry'}</div>
          <h2 className="hero-title text-[32px] leading-tight">{isJoin ? app.name : app.org}</h2>
          <div className="flex items-center gap-4 mt-3 text-[13px] text-[#0d2a20]/70 flex-wrap">
            <span className="flex items-center gap-1"><Mail size={12} /> {app.email}</span>
            <span className="flex items-center gap-1"><Calendar size={12} /> Submitted {fmtDate(app.created_at)}</span>
            {isJoin && app.invited_at && <span className="flex items-center gap-1 text-[#e0684d]"><Send size={12} /> Invited {fmtDate(app.invited_at)}</span>}
            {isJoin && app.activated_at && <span className="flex items-center gap-1 text-[#154a1c]"><CheckCircle2 size={12} /> Activated {fmtDate(app.activated_at)}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isJoin && <BucketPill bucket={app.bucket} />}
          <Pill status={app.status} />
          <select value={app.status} onChange={e => onStatus(app.id, e.target.value)} className="px-3 py-1.5 rounded-md bg-white border text-[13px]" style={{ borderColor: 'rgba(13,42,32,0.15)' }}>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          {isJoin && app.status === 'approved' && !app.activated_at && (
            <button onClick={() => onResend(app.id)} disabled={resending} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md btn-coral text-[12px] disabled:opacity-60"><Send size={12} /> {resending ? 'Sending…' : 'Resend invite'}</button>
          )}
          {hasCv && (
            <button
              onClick={downloadCv}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md btn-forest text-[12px]"
              data-testid="admin-cv-download-btn"
              title={app.cv_filename || 'Download CV'}
            >
              <Download size={12} /> Download CV
            </button>
          )}
          <button
            onClick={() => onDelete && onDelete(app)}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-[12px]"
            style={{ background: '#f3d6d1', color: '#7a1f13' }}
            data-testid="admin-delete-app-btn"
          >
            <Trash2 size={12} /> Delete permanently
          </button>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {entries.map(([k, v]) => (
          <div key={k} className="py-3 px-4 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.08)' }}>
            <div className="text-[11px] uppercase tracking-wider text-[#0d2a20]/50 mb-1">{k}</div>
            <div className="text-[14px] text-[#0d2a20] break-words">{Array.isArray(v) ? (v.length ? v.join(', ') : '—') : (typeof v === 'boolean' ? (v ? 'Yes' : 'No') : (v && typeof v === 'object' ? JSON.stringify(v) : (String(v ?? '') || '—')))}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const JoinRow = ({ app, onOpen, onStatus, onResend, resending }) => (
  <tr className="border-b last:border-b-0 hover:bg-[#f6f0dc]/70 transition-colors cursor-pointer" style={{ borderColor: 'rgba(13,42,32,0.08)' }} onClick={() => onOpen(app)}>
    <td className="py-3 px-4 font-medium text-[14px]">{app.name}</td>
    <td className="py-3 px-4 text-[13px] text-[#0d2a20]/75">{app.email}</td>
    <td className="py-3 px-4 text-[12px] text-[#0d2a20]/60">{fmtDate(app.created_at)}</td>
    <td className="py-3 px-4"><BucketPill bucket={app.bucket} /></td>
    <td className="py-3 px-4" onClick={e => e.stopPropagation()}>
      <div className="flex items-center gap-2">
        <select value={app.status} onChange={e => onStatus(app.id, e.target.value)} className="px-2 py-1 rounded-md bg-white border text-[12px]" style={{ borderColor: 'rgba(13,42,32,0.15)' }}>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {app.status === 'approved' && !app.activated_at && (
          <button onClick={() => onResend(app.id)} disabled={resending} className="inline-flex items-center gap-1 px-2 py-1 rounded-md btn-coral text-[11px] disabled:opacity-60"><Send size={11} />Resend</button>
        )}
      </div>
    </td>
  </tr>
);

const PartnerRow = ({ app, onOpen, onStatus }) => (
  <tr className="border-b last:border-b-0 hover:bg-[#f6f0dc]/70 transition-colors cursor-pointer" style={{ borderColor: 'rgba(13,42,32,0.08)' }} onClick={() => onOpen(app)}>
    <td className="py-3 px-4 font-medium text-[14px]">{app.org}</td>
    <td className="py-3 px-4 text-[13px] text-[#0d2a20]/75">{app.email}</td>
    <td className="py-3 px-4 text-[12px] text-[#0d2a20]/60">{fmtDate(app.created_at)}</td>
    <td className="py-3 px-4"><Pill status={app.status} /></td>
    <td className="py-3 px-4" onClick={e => e.stopPropagation()}>
      <select value={app.status} onChange={e => onStatus(app.id, e.target.value)} className="px-2 py-1 rounded-md bg-white border text-[12px]" style={{ borderColor: 'rgba(13,42,32,0.15)' }}>
        {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
      </select>
    </td>
  </tr>
);

const TABS = [
  { key: 'applicant',        label: 'Applicants',       icon: Users,      scope: 'join' },
  { key: 'waitlisted',       label: 'Waitlisted',       icon: Hourglass,  scope: 'join' },
  { key: 'approved_pending', label: 'Approved · pending', icon: Clock,     scope: 'join' },
  { key: 'active_member',    label: 'Active members',   icon: UserCheck,  scope: 'join' },
  { key: 'declined',         label: 'Declined',         icon: UserX,      scope: 'join' },
  { key: 'members',          label: 'Members management', icon: UserCheck, scope: 'members' },
  { key: 'partner',          label: 'Partner inquiries',icon: Building2,  scope: 'partner' },
  { key: 'cms_network_updates', label: 'Network Updates', icon: Megaphone, scope: 'cms' },
  { key: 'cms_webinars',     label: 'Masterclasses',    icon: Video,      scope: 'cms' },
  { key: 'cms_organisations',label: 'Organisations',    icon: Building2,  scope: 'cms' },
  { key: 'cms_opportunities',label: 'Opportunities',    icon: Briefcase,  scope: 'cms' },
  { key: 'cms_email_templates', label: 'Email templates', icon: Mail,      scope: 'cms' },
  { key: 'settings',         label: 'Settings',         icon: SettingsIcon, scope: 'settings' },
];

const AdminDashboard = () => {
  const nav = useNavigate();
  const [tab, setTab] = useState('applicant');
  const [joinList, setJoinList] = useState([]);
  const [partnerList, setPartnerList] = useState([]);
  const [stats, setStats] = useState({ applicants: 0, waitlisted: 0, approved_pending: 0, active_members: 0, declined: 0, partner_total: 0, partner_new: 0 });
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resendingId, setResendingId] = useState(null);
  const [lastActivation, setLastActivation] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { scope, app }

  const requestDeleteApplication = (appRow) => setDeleteConfirm({ scope: currentScope, app: appRow });

  const confirmDeleteApplication = async () => {
    if (!deleteConfirm) return;
    const { scope, app } = deleteConfirm;
    try {
      await adminApi.delete(`/admin/applications/${scope}/${app.id}?confirm=true`);
      toast({ title: 'Application deleted', description: 'All personal data has been removed.' });
      if (scope === 'join') setJoinList(list => list.filter(a => a.id !== app.id));
      else setPartnerList(list => list.filter(a => a.id !== app.id));
      if (selected?.id === app.id) setSelected(null);
      setDeleteConfirm(null);
      const s = await adminApi.get('/admin/stats'); setStats(s.data);
    } catch (e) { toast({ title: 'Delete failed', description: e?.response?.data?.detail || 'Try again.' }); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [j, p, s] = await Promise.all([
        adminApi.get('/admin/applications/join'),
        adminApi.get('/admin/applications/partner'),
        adminApi.get('/admin/stats'),
      ]);
      setJoinList(j.data); setPartnerList(p.data); setStats(s.data);
    } catch (err) {
      if (err?.response?.status === 401) { clearAdminToken(); nav('/admin', { replace: true }); }
      else toast({ title: 'Failed to load', description: 'Please retry.' });
    } finally { setLoading(false); }
  }, [nav]);
  useEffect(() => {
    if (!getAdminToken()) { nav('/admin', { replace: true }); return; }
    load();
  }, [load, nav]);

  const logout = () => { clearAdminToken(); nav('/admin', { replace: true }); };

  const currentScope = TABS.find(t => t.key === tab)?.scope || 'join';

  const updateStatus = async (id, status) => {
    try {
      const url = `/admin/applications/${currentScope}/${id}`;
      const { data } = await adminApi.patch(url, { status });
      if (currentScope === 'join') {
        setJoinList(list => list.map(a => a.id === id ? { ...a, ...data } : a));
        if (data.invite) {
          setLastActivation({ email: data.email, url: data.invite.activation_url });
          const er = data.invite.email_result || {};
          if (er.error) {
            toast({ title: 'Approved — email FAILED', description: `Resend error: ${String(er.error).slice(0, 180)}` });
          } else if (er.skipped) {
            toast({ title: 'Approved — email skipped', description: 'RESEND_API_KEY missing on the server.' });
          } else if (er.relayed) {
            toast({ title: 'Approved — email relayed', description: `Sent to owner (${er.relayed_to}); please forward to ${er.intended_to}.` });
          } else {
            toast({ title: 'Approved & invitation sent', description: `Email dispatched to ${data.email}.` });
          }
        } else {
          toast({ title: 'Status updated', description: `→ ${status}` });
        }
      } else {
        setPartnerList(list => list.map(a => a.id === id ? { ...a, ...data } : a));
        toast({ title: 'Status updated', description: `→ ${status}` });
      }
      if (selected && selected.id === id) setSelected({ ...selected, ...data });
      const s = await adminApi.get('/admin/stats'); setStats(s.data);
    } catch (err) {
      toast({ title: 'Update failed', description: err?.response?.data?.detail || 'Try again.' });
    }
  };

  const resend = async (id) => {
    setResendingId(id);
    try {
      const { data } = await adminApi.post(`/admin/applications/join/${id}/resend-invite`);
      const email = joinList.find(a => a.id === id)?.email;
      setLastActivation({ email, url: data.activation_url });
      const er = data.email_result || {};
      if (er.error) {
        toast({ title: 'Resend — email FAILED', description: `Resend error: ${String(er.error).slice(0, 180)}` });
      } else if (er.skipped) {
        toast({ title: 'Resend — email skipped', description: 'RESEND_API_KEY missing on the server.' });
      } else if (er.relayed) {
        toast({ title: 'Invitation relayed', description: `Sent to owner (${er.relayed_to}); please forward to ${er.intended_to}.` });
      } else {
        toast({ title: 'Invitation resent', description: `Email dispatched to ${email}.` });
      }
    } catch (err) {
      toast({ title: 'Resend failed', description: err?.response?.data?.detail || 'Try again.' });
    } finally { setResendingId(null); }
  };

  const openDetail = async (row) => {
    try {
      const url = `/admin/applications/${currentScope}/${row.id}`;
      const { data } = await adminApi.get(url);
      setSelected(data);
    } catch { toast({ title: 'Could not open' }); }
  };

  const exportCsv = async () => {
    // Choose the right endpoint for the active tab.
    let path = null, label = null;
    if (currentScope === 'partner') { path = '/admin/applications/partner/export.csv'; label = 'partner applications'; }
    else if (tab === 'active_member') { path = '/admin/members/export.csv'; label = 'active members'; }
    else if (currentScope === 'join') { path = '/admin/applications/join/export.csv'; label = 'network applications'; }
    if (!path) return;
    try {
      const res = await adminApi.get(path, { responseType: 'blob' });
      const disposition = res.headers?.['content-disposition'] || '';
      const match = /filename="?([^";]+)"?/i.exec(disposition);
      const filename = match ? match[1] : `vemoye-${label.replace(/\s+/g,'-')}-${new Date().toISOString().slice(0,10)}.csv`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
      toast({ title: 'Export ready', description: `Downloading ${label} CSV.` });
    } catch (e) { toast({ title: 'Export failed', description: 'Please try again.' }); }
  };
  const canExport = currentScope === 'partner' || currentScope === 'join';

  const list = currentScope === 'partner' ? partnerList : (currentScope === 'join' ? joinList.filter(a => a.bucket === tab) : []);

  const renderContent = () => {
    if (currentScope === 'cms') {
      if (tab === 'cms_webinars') return <CMSWebinars />;
      if (tab === 'cms_organisations') return <CMSOrganisations />;
      if (tab === 'cms_opportunities') return <CMSOpportunities />;
      if (tab === 'cms_email_templates') return <CMSEmailTemplates />;
      if (tab === 'cms_network_updates') return <CMSNetworkUpdates />;
    }
    if (currentScope === 'settings') return <CMSSettings />;
    if (currentScope === 'members') return <CMSMembers />;
    if (selected) {
      return <DetailPanel scope={currentScope} app={selected} onBack={() => setSelected(null)} onStatus={updateStatus} onResend={resend} resending={resendingId === selected.id} onDelete={requestDeleteApplication} />;
    }
    return (
      <div className="rounded-lg overflow-hidden" style={{ background: '#fbf8ef' }}>
        {loading ? (
          <div className="p-10 text-center text-[14px] text-[#0d2a20]/60">Loading…</div>
        ) : list.length === 0 ? (
          <div className="p-10 text-center text-[14px] text-[#0d2a20]/60">Nothing here yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#0d2a20]/60" style={{ background: '#ece5d3' }}>
                  <th className="py-3 px-4">{currentScope === 'partner' ? 'Organisation' : 'Name'}</th>
                  <th className="py-3 px-4">Email</th>
                  <th className="py-3 px-4">Submitted</th>
                  <th className="py-3 px-4">{currentScope === 'partner' ? 'Status' : 'State'}</th>
                  <th className="py-3 px-4">Change</th>
                </tr>
              </thead>
              <tbody>
                {list.map(a => currentScope === 'partner'
                  ? <PartnerRow key={a.id} app={a} onOpen={openDetail} onStatus={updateStatus} />
                  : <JoinRow key={a.id} app={a} onOpen={openDetail} onStatus={updateStatus} onResend={resend} resending={resendingId === a.id} />
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="section-eyebrow text-[#0d2a20]/60 mb-2">Admin</div>
          <h1 className="hero-title text-[44px] md:text-[64px] leading-none">Applications <span className="font-serif-i text-[#e0684d]">dashboard</span></h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="inline-flex items-center gap-2 px-4 py-2 rounded-md btn-ghost text-[13px]"><RefreshCw size={14} /> Refresh</button>
          {canExport && (
            <button onClick={exportCsv} data-testid="admin-export-csv-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-md btn-gold text-[13px]"><FileDown size={14} /> Export CSV</button>
          )}
          <button onClick={logout} className="inline-flex items-center gap-2 px-4 py-2 rounded-md btn-green text-[13px]"><LogOut size={14} /> Sign out</button>
        </div>
      </div>

      {lastActivation && (
        <div className="mb-6 rounded-lg p-4 flex items-start gap-3" style={{ background: '#fde5c7' }}>
          <Send size={16} className="mt-0.5 text-[#8a4a10]" />
          <div className="flex-1">
            <div className="text-[13px] font-medium text-[#8a4a10]">Activation link generated for {lastActivation.email}</div>
            <div className="text-[12px] text-[#8a4a10]/85 break-all mt-1">{lastActivation.url}</div>
          </div>
          <button onClick={() => { navigator.clipboard.writeText(lastActivation.url); toast({ title: 'Copied' }); }} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white text-[12px] border" style={{ borderColor: 'rgba(138,74,16,0.3)' }}><Copy size={12} /> Copy</button>
          <button onClick={() => setLastActivation(null)} className="text-[12px] text-[#8a4a10] hover:underline">Dismiss</button>
        </div>
      )}

      <div className="grid md:grid-cols-5 gap-4 mb-8">
        {[
          { l: 'Applicants', v: stats.applicants, icon: Users },
          { l: 'Waitlisted', v: stats.waitlisted, icon: Hourglass },
          { l: 'Approved · pending', v: stats.approved_pending, icon: Clock, hi: true },
          { l: 'Active members', v: stats.active_members, icon: UserCheck },
          { l: 'Declined', v: stats.declined, icon: UserX },
        ].map((s, i) => {
          const Ic = s.icon;
          return (
            <div key={i} className="rounded-lg p-5" style={{ background: '#fbf8ef' }}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-[12px] text-[#0d2a20]/60">{s.l}</span>
                <Ic size={16} className={s.hi ? 'text-[#e0684d]' : 'text-[#0d2a20]/40'} />
              </div>
              <div className="text-[32px] font-medium tracking-tight">{s.v}</div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {TABS.map(t => {
          const active = tab === t.key;
          const Ic = t.icon;
          return (
            <button key={t.key} data-testid={`admin-tab-${t.key}`} onClick={() => { setTab(t.key); setSelected(null); }} className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-[13px] transition-colors ${active ? 'bg-[#0f3d2e] text-[#f4efe4]' : 'bg-[#fbf8ef] text-[#0d2a20] hover:bg-[#ece5d3]'}`}><Ic size={14} /> {t.label}</button>
          );
        })}
      </div>

      {renderContent()}

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(13,42,32,0.5)' }}>
          <div className="rounded-lg p-6 max-w-md w-full" style={{ background: '#fbf8ef' }} data-testid="admin-delete-app-confirm">
            <h3 className="text-[20px] font-medium mb-2">Permanently delete this record?</h3>
            <p className="text-[14px] text-[#0d2a20]/75 mb-4">This will permanently delete <b>{deleteConfirm.app.name || deleteConfirm.app.org || deleteConfirm.app.email}</b>'s application and associated personal data from the Vemoye platform. If they had an activated member account it will also be removed. Aggregate data (registration counts, poll totals) will be kept anonymously. This action cannot be undone.</p>
            <div className="flex items-center gap-2">
              <button onClick={confirmDeleteApplication} data-testid="admin-delete-app-confirm-btn" className="px-4 py-2 rounded-md text-[13px]" style={{ background: '#7a1f13', color: 'white' }}>Yes, delete permanently</button>
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 rounded-md btn-ghost text-[13px]">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
